import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ProfileAdd } from "iconsax-react";
import { ModalForm, Input, Button, SearchableSelect, DatePicker, Prompt, PhoneInput } from "@/shared/ui";
import { useLocale, useFieldAvailability } from "@/shared/hooks";
import { useGetRegionsQuery, type RegionResponse } from "@/shared/api/common";
import { extractErrorMessage, formatDateToISO } from "@/shared/utils";
import { useCreateVolunteerMutation } from "../api";
import { createVolunteerSchema, type CreateVolunteerFormValues } from "../validation";

const EMAIL_FORMAT_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface Props {
  onClose: () => void;
  onCreated: (volunteerId: number) => void;
}

interface PromptState {
  title: string;
  text: string;
  variant: "success" | "error";
}

export default function VolunteerCreateModal({ onClose, onCreated }: Props) {
  const { t } = useTranslation("VolunteersPage");
  const locale = useLocale();
  const [createVolunteer] = useCreateVolunteerMutation();
  const { data: regions = [] } = useGetRegionsQuery();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [prompt, setPrompt] = useState<PromptState | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<CreateVolunteerFormValues>({
    resolver: zodResolver(createVolunteerSchema),
    defaultValues: {
      surname: "",
      name: "",
      father_name: "",
      email: "",
      phone: "",
      date_of_birth: "",
    },
  });

  const regionId = watch("region_id");
  const dateOfBirth = watch("date_of_birth");
  const emailValue = watch("email") ?? "";

  const emailAvailability = useFieldAvailability({
    resource: "volunteer",
    field: "email",
    value: emailValue,
    isFormatValid: EMAIL_FORMAT_RE.test(emailValue),
  });
  const isEmailTaken = emailAvailability.state === "taken";

  const selectedRegion = useMemo(
    function findSelectedRegion() {
      if (!regionId) return null;
      return regions.find((r) => r.id === regionId) ?? null;
    },
    [regionId, regions],
  );

  const dateValue = useMemo(
    function parseDateValue() {
      if (!dateOfBirth) return null;
      const parsed = new Date(dateOfBirth);
      return isNaN(parsed.getTime()) ? null : parsed;
    },
    [dateOfBirth],
  );

  async function onSubmit(data: CreateVolunteerFormValues) {
    if (isSubmitting) return;
    try {
      setIsSubmitting(true);
      const payload = {
        ...data,
        father_name: data.father_name || undefined,
        date_of_birth: data.date_of_birth,
      };
      const result = await createVolunteer(payload).unwrap();
      onCreated(result.id);
      onClose();
    } catch (err: unknown) {
      setPrompt({
        title: t("prompt.error.title"),
        text: extractErrorMessage(err, "prompt.error.createFailed", t),
        variant: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <ModalForm
      icon={ProfileAdd}
      onClose={onClose}
      width="w-lg"
      footer={
        <div className="grid grid-cols-[2fr_3fr] gap-3">
          <Button variant="secondary" size="huge" onClick={onClose} type="button" disabled={isSubmitting}>
            {t("createModal.buttons.cancel")}
          </Button>
          <Button
            form="volunteer-create-form"
            variant="primary"
            size="huge"
            type="submit"
            disabled={isSubmitting || isEmailTaken}
            textDuringDisabled={isEmailTaken ? t("createModal.rules.emailTaken") : undefined}
            fullWidthTooltip>
            {isSubmitting ? t("createModal.buttons.loading") : t("createModal.buttons.submit")}
          </Button>
        </div>
      }
    >
      <form id="volunteer-create-form" onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
        <div className="flex flex-col gap-2 shrink-0 pb-5 border-b surface-base-stroke">
          <h4 className="text-display-2xs content-base-primary">{t("createModal.title")}</h4>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Input
            label={t("createModal.fields.surname")}
            placeholder={t("createModal.placeholders.surname")}
            hasRequiredLabel
            error={errors.surname?.message ? t(errors.surname.message) : undefined}
            {...register("surname")}
          />
          <Input
            label={t("createModal.fields.name")}
            placeholder={t("createModal.placeholders.name")}
            hasRequiredLabel
            error={errors.name?.message ? t(errors.name.message) : undefined}
            {...register("name")}
          />
        </div>

        <Input
          label={t("createModal.fields.fatherName")}
          placeholder={t("createModal.placeholders.fatherName")}
          error={errors.father_name?.message ? t(errors.father_name.message) : undefined}
          {...register("father_name")}
        />

        <div className="grid grid-cols-2 items-start gap-4">
          <Input
            label={t("createModal.fields.email")}
            placeholder={t("createModal.placeholders.email")}
            hasRequiredLabel
            type="email"
            error={
              errors.email?.message
                ? t(errors.email.message)
                : isEmailTaken
                  ? t("uniqueness.taken.email")
                  : undefined
            }
            {...register("email")}
          />
          <PhoneInput
            label={t("createModal.fields.phone")}
            hasRequiredLabel
            value={watch("phone")}
            onChange={(value) => setValue("phone", value, { shouldDirty: true })}
            error={errors.phone?.message ? t(errors.phone.message) : undefined}
          />
        </div>

        <SearchableSelect<RegionResponse>
          label={t("createModal.fields.region")}
          placeholder={t("createModal.placeholders.region")}
          searchPlaceholder={t("createModal.placeholders.searchRegion")}
          hasRequiredLabel
          options={regions}
          value={selectedRegion}
          onChange={(region) =>
            setValue("region_id", region?.id ?? (undefined as unknown as number), { shouldValidate: true })
          }
          searchKeys={["name_ru", "name_kk"]}
          displayKey={`name_${locale}` as keyof RegionResponse}
          error={errors.region_id?.message ? t(errors.region_id.message) : undefined}
        />

        <DatePicker
          label={t("createModal.fields.dateOfBirth")}
          placeholder={t("createModal.placeholders.dateOfBirth")}
          hasRequiredLabel
          locale={locale}
          value={dateValue}
          maxDate={new Date()}
          onChange={(date) => setValue("date_of_birth", date ? formatDateToISO(date) : "", { shouldValidate: true })}
          error={errors.date_of_birth?.message ? t(errors.date_of_birth.message) : undefined}
        />
      </form>

      {prompt && (
        <Prompt
          title={prompt.title}
          text={prompt.text}
          variant={prompt.variant}
          onClose={() => setPrompt(null)}
          namespace="VolunteersPage"
        />
      )}
    </ModalForm>
  );
}
